import React from 'react';
import { GameState, Decisions } from '../types/game';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ArrowRightIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon } from 'lucide-react';
interface QuarterlyReportProps {
  gameState: GameState;
  decisions: Decisions | null;
  onContinue: () => void;
}
export function QuarterlyReport({
  gameState,
  decisions,
  onContinue
}: QuarterlyReportProps) {
  const currentHistory = gameState.history[gameState.history.length - 1];
  const prevHistory = gameState.history[gameState.history.length - 2];
  // If no previous history (start of game), just show current
  const prevCash = prevHistory ? prevHistory.cash : gameState.cash;
  const prevRevenue = prevHistory ? prevHistory.revenue : 0;
  const prevNetIncome = prevHistory ? prevHistory.netIncome : 0;
  const cashDelta = gameState.cash - prevCash;
  const revenueDelta = gameState.revenue - prevRevenue;
  const netIncomeDelta = gameState.netIncome - prevNetIncome;
  const formatMoney = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const formatDelta = (val: number) => {
    const sign = val > 0 ? '+' : '';
    return `${sign}${formatMoney(val)}`;
  };
  const DeltaIndicator = ({
    val
  }: {
    val: number;
  }) => {
    if (val > 0) return <TrendingUpIcon className="w-4 h-4 text-success ml-2" data-id="element-255" />;
    if (val < 0) return <TrendingDownIcon className="w-4 h-4 text-danger ml-2" data-id="element-256" />;
    return <MinusIcon className="w-4 h-4 text-text-secondary ml-2" data-id="element-257" />;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4 animate-fade-in" data-id="element-258">
      <Card className="w-full max-w-4xl border-primary/20 shadow-glow-teal-lg flex flex-col max-h-[90vh]" data-id="element-259">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-6 mb-6" data-id="element-260">
          <div data-id="element-261">
            <div className="text-xs font-mono text-primary mb-1 uppercase tracking-wider" data-id="element-262">
              Quarterly Report
            </div>
            <h1 className="text-3xl font-bold text-text-primary" data-id="element-263">
              Q{(gameState.turn - 1) % 4 + 1} · Year{' '}
              {Math.ceil(gameState.turn / 4)} Results
            </h1>
          </div>
          <div className="text-right" data-id="element-264">
            <div className="text-xs text-text-secondary uppercase tracking-wider mb-1" data-id="element-265">
              Net Result
            </div>
            <div className={`text-2xl font-mono font-bold ${gameState.netIncome >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-266">
              {gameState.netIncome >= 0 ? '+' : ''}
              {formatMoney(gameState.netIncome)}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 overflow-y-auto" data-id="element-267">
          {/* Financials Column */}
          <div className="space-y-4" data-id="element-268">
            <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider border-b border-border pb-2" data-id="element-269">
              Financials
            </h3>

            <div className="bg-surface-highlight/50 p-4 rounded-lg border border-border" data-id="element-270">
              <div className="text-xs text-text-secondary mb-1" data-id="element-271">Revenue</div>
              <div className="flex items-end justify-between" data-id="element-272">
                <span className="text-xl font-mono font-bold text-text-primary" data-id="element-273">
                  {formatMoney(gameState.revenue)}
                </span>
                <div className="flex items-center text-xs" data-id="element-274">
                  <span className={revenueDelta >= 0 ? 'text-success' : 'text-danger'} data-id="element-275">
                    {formatDelta(revenueDelta)}
                  </span>
                  <DeltaIndicator val={revenueDelta} data-id="element-276" />
                </div>
              </div>
            </div>

            <div className="bg-surface-highlight/50 p-4 rounded-lg border border-border" data-id="element-277">
              <div className="text-xs text-text-secondary mb-1" data-id="element-278">Expenses</div>
              <div className="flex items-end justify-between" data-id="element-279">
                <span className="text-xl font-mono font-bold text-text-primary" data-id="element-280">
                  {formatMoney(gameState.expenses)}
                </span>
                <div className="text-xs text-text-secondary" data-id="element-281">Burn Rate</div>
              </div>
            </div>

            <div className="bg-surface-highlight/50 p-4 rounded-lg border border-border" data-id="element-282">
              <div className="text-xs text-text-secondary mb-1" data-id="element-283">
                Cash Balance
              </div>
              <div className="flex items-end justify-between" data-id="element-284">
                <span className="text-xl font-mono font-bold text-text-primary" data-id="element-285">
                  {formatMoney(gameState.cash)}
                </span>
                <div className="flex items-center text-xs" data-id="element-286">
                  <span className={cashDelta >= 0 ? 'text-success' : 'text-danger'} data-id="element-287">
                    {formatDelta(cashDelta)}
                  </span>
                  <DeltaIndicator val={cashDelta} data-id="element-288" />
                </div>
              </div>
            </div>
          </div>

          {/* Operational Column */}
          <div className="space-y-4" data-id="element-289">
            <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider border-b border-border pb-2" data-id="element-290">
              Operations
            </h3>

            <div className="bg-surface-highlight/50 p-4 rounded-lg border border-border" data-id="element-291">
              <div className="text-xs text-text-secondary mb-1" data-id="element-292">
                Product Quality
              </div>
              <div className="flex items-end justify-between" data-id="element-293">
                <span className="text-xl font-mono font-bold text-text-primary" data-id="element-294">
                  {Math.round(gameState.productQuality)}/100
                </span>
                <div className="w-24 h-1.5 bg-surface rounded-full overflow-hidden ml-4 mb-1.5" data-id="element-295">
                  <div className="h-full bg-primary transition-all" style={{
                  width: `${gameState.productQuality}%`
                }} data-id="element-296" />
                </div>
              </div>
            </div>

            <div className="bg-surface-highlight/50 p-4 rounded-lg border border-border" data-id="element-297">
              <div className="text-xs text-text-secondary mb-1" data-id="element-298">Headcount</div>
              <div className="flex items-end justify-between" data-id="element-299">
                <span className="text-xl font-mono font-bold text-text-primary" data-id="element-300">
                  {gameState.engineers + gameState.sales}
                </span>
                <div className="text-xs text-text-secondary" data-id="element-301">
                  {gameState.engineers} Eng · {gameState.sales} Sales
                </div>
              </div>
            </div>
          </div>

          {/* Decisions Recap */}
          <div className="space-y-4" data-id="element-302">
            <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider border-b border-border pb-2" data-id="element-303">
              Decisions Recap
            </h3>

            {decisions && <div className="space-y-3" data-id="element-304">
                <div className="flex justify-between items-center text-sm" data-id="element-305">
                  <span className="text-text-secondary" data-id="element-306">Unit Price</span>
                  <span className="font-mono text-primary" data-id="element-307">
                    ${decisions.unitPrice}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm" data-id="element-308">
                  <span className="text-text-secondary" data-id="element-309">New Hires</span>
                  <span className="font-mono text-text-primary" data-id="element-310">
                    +{decisions.newEngineers} Eng, +{decisions.newSales} Sales
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm" data-id="element-311">
                  <span className="text-text-secondary" data-id="element-312">Salary</span>
                  <span className={`font-mono ${decisions.salaryMultiplier > 1 ? 'text-success' : decisions.salaryMultiplier < 1 ? 'text-danger' : 'text-text-primary'}`} data-id="element-313">
                    {Math.round(decisions.salaryMultiplier * 100)}%
                  </span>
                </div>
              </div>}

            <div className="mt-6 p-4 bg-primary/10 rounded border border-primary/20" data-id="element-314">
              <p className="text-sm text-primary italic" data-id="element-315">
                "
                {gameState.netIncome > 0 ? "Solid quarter. We're growing sustainably." : "We're burning cash. Keep an eye on the runway."}
                "
              </p>
            </div>
          </div>
        </div>

        {/* Footer Action */}
        <div className="pt-6 border-t border-border flex justify-end" data-id="element-316">
          <Button size="lg" onClick={onContinue} className="group" data-id="element-317">
            Go to Dashboard
            <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-318" />
          </Button>
        </div>
      </Card>
    </div>;
}